<template>
  <tr class="border border-start-0 border-end-0 shadow-sm align-top">
    <td>
        <Select2
            container-class="form-group mt-auto"
            :multiple="false"
            :show-search="true"
            :selectClass="'form-select form-control-lg form-select-solid'"
            :name="`invoice-item-${index}`"
            @update:modelValue="handleSelected"
            :isRequired="true"
            :placeholderText="$t('invoice.placeholder.item.name')"
            v-model="itemData.product_id">
            <option v-for="(item, index) in filterProductSaleElements()" :key="index" :value="item.id">{{ item.declination_title }}</option>
        </Select2>
    </td>
    <td>
        <Input
            container-class="form-group mt-auto"
            :name="`invoice-item-${index}-quantity`"
            type="number"
            labelClass="form-label fs-6 text-black"
            :inputClass="'form-control form-control-lg form-control-solid'"
            :isRequired="true"
            size="lg"
            :min="1"
            v-model="itemData.quantity"
        />
    </td>
    <td>
        <Input
            container-class="form-group mt-auto"
            :name="`invoice-item-${index}-price`"
            type="number"
            labelClass="form-label fs-6 text-black"
            :inputClass="'form-control form-control-lg form-control-solid'"
            :isRequired="true" size="lg"
            :placeholder="$t('invoice.placeholder.item.price')"
            v-model="itemData.price"
            :min="1"
        />
    </td>
    <td>
        <div class="h-100">
            <div class="input-group">
                <input
                    v-if="itemData.discount_type === discountType.percent.value"
                    type="number"
                    class="form-control form-control-solid font-weight-600"
                    min="0" max="100"
                    v-model="itemData.discount_value"
                    :min="99"
                    :max="1"
                    placeholder="">
                <input v-else class="form-control form-control-solid font-weight-600" v-model="itemData.discount_value">
                <span class="input-group-text">
                    <select
                        :name="`invoice-item-${index}-discount`"
                        style="padding: 0;border:0px; background-color: var(--bs-gray-200)"
                        v-model="itemData.discount_type"
                        class="'form-select form-select-transparent text-dark fs-7 lh-1 fw-bold py-0 ps-3 select2-hidden-accessible'"
                    >
                        <option :value="discountType.percent.value"> {{ discountType.percent.symbol }} </option>
                        <option :value="discountType.fixed.value"> {{ symbol }} </option>
                    </select>
                </span>
            </div>
        </div>
    </td>
    <td>
        <Select2
            container-class="col-12 tax-input-container"
            :multiple="true"
            :show-search="true"
            selectClass="form-control form-select form-select-solid rounded-end-0 border-start"
            :name="`purchase-product-${index}-tax`"
            :placeholderText="$t('invoice.placeholder.item.taxes')"
            v-model="itemData.taxes"
            :isRequired="false"
            :IsInputGroup="$can(abilities.CREATE_TAX)"
            icon="bi bi-plus"
            :clickAddon="openTaxModal">
            <option v-for="(tax, index) in taxes" :key="index" :value="tax.id">
                {{ tax.title + ` (${tax.amount}` }}
                {{ tax.type === discountType.percent.value ? discountType.percent.symbol+')' : symbol + ')'}}
            </option>
        </Select2>
    </td>
    <td>
        <div class="form-control border-0 fs-1">
            {{ $formatLocalAmount(parseFloat(total)) ?? 0 }}
        </div>
    </td>
    <th>
        <a class="btn btn-sm btn-icon btn-danger p-5" @click.prevent="deleteRow(index)">
            <i class="bi bi-trash-fill fs-5"></i>
        </a>
    </th>
</tr>

  <CreateTax @load="afterCreateTax" :id="'itemTaxCreateModal'+index"/>
</template>

<script setup>

import Select2 from "@/components/Select2.vue";
import {computed, defineEmits, defineProps, ref, watch} from "vue";
import Input from "@/components/Input.vue";
import {useDiscountType} from "@/composables/discountType";
import {useStore} from "vuex";
import CreateTax from "@/components/Tax/CreateTax.vue";
import {$formatLocalAmount} from "@/mixins/Helpers";
import abilities from "@/composables/abilities";

const store = useStore()
const emits = defineEmits(["deleteItem"])
const props = defineProps({
  item: {
    type: Object,
    default: () => ({
      product_id: '',
      quantity: 1,
      price: 0,
      description: '',
      discount_type: 'percent',
      discount_value: 0,
      taxes: [],
      total: 0
    })
  },
  rate: { type: Number, default: 1 },
  symbol: String,
  id: Number,
  index: Number,
  selectedItems: Array
})
const {discountType} = useDiscountType()


const itemData = ref(props.item)

const taxes = computed(() => store.getters['taxes/getTaxes'])
const items = computed(() => store.getters['products/getProductSaleElements'])
const total = computed(() =>  {
  const taxesSelected = taxes.value.filter(tax => itemData.value.taxes?.includes(tax.id));
  let totalPrice = itemData.value.price * itemData.value.quantity;

  if(itemData.value.discount_value) {
    totalPrice = totalPrice - applyDiscount(itemData.value.discount_type, itemData.value.discount_value, totalPrice)
  }

  if (taxesSelected.length > 0) {
    const totalTaxesPrice = taxesSelected.reduce((accumulator, tax) => {
      if (tax.type === 'amount') {
        accumulator.accAmount += parseFloat(tax.amount);
      } else {
        accumulator.accPercent += (totalPrice * parseFloat(tax.amount) / 100);
      }
      return accumulator;
    }, { accAmount: 0, accPercent: 0 });
    totalPrice = totalPrice + totalTaxesPrice.accAmount + totalTaxesPrice.accPercent;
  }

  return totalPrice || 0;
})

watch(() =>  props.rate, () => {
  handleSelected(itemData.value.product_id)
})

watch(() => total.value, (val) => {
  itemData.value.total = val
}, {immediate: true})

watch(() => itemData.value.discount_value, (val) => {
  itemData.value.total = val
}, {immediate: true})

function applyDiscount (discountType, discountVal, amount) {
  if (discountType !== 'amount') {
    return (amount * parseFloat(discountVal) / 100)
  }
  return discountVal
}
function handleSelected (productId) {
  if (productId) {
    let product = items.value.find(el => el.id === productId)
    let rate = 1
    if(props.rate) {
      rate = props.rate
    }
    itemData.value.price = (parseInt(product?.selling_price) / rate).toFixed(0)
    itemData.value.taxes = props.item?.taxes
  }
}
function openTaxModal() {
  $("#itemTaxCreateModal"+props.index).modal('toggle')
}
function filterProductSaleElements() {
  const current = itemData.value.product_id
  const idsWithoutCur = props.selectedItems.filter(id => id != current)
  return items.value.filter(product => !idsWithoutCur.includes(product.id) );
}
function afterCreateTax(tax) {
  $("#itemTaxCreateModal"+props.index).modal('toggle')
  itemData.value.taxes.push(tax?.id)
}
function deleteRow(index) {
  emits('deleteItem', index)
}
</script>

<style scoped>
</style>
